import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * F04 — reads voicemail_flag.pending for the given senior + subscribes to
 * realtime UPDATE events. RLS scopes SELECT to caregivers' own seniors only.
 *
 * Returns:
 *   `pending`: true|false when state is known, null while loading or when no
 *              row exists yet (= sharing not yet enabled or device hasn't
 *              reported). The header indicator treats null as "hidden/grey".
 */
export function useVoicemailFlag(seniorId: string | undefined) {
  const [pending, setPending] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!seniorId) {
      setPending(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from('voicemail_flag')
        .select('pending')
        .eq('senior_user_id', seniorId)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        console.warn('[useVoicemailFlag] fetch error:', error.message);
        setPending(null);
      } else {
        setPending(data?.pending === true ? true : data?.pending === false ? false : null);
      }
      setLoading(false);
    };

    setLoading(true);
    fetchOnce();

    // Realtime UPDATEs on voicemail_flag — RLS gates which rows the caregiver
    // can subscribe to.
    const channel = supabase
      .channel(`voicemail_flag_${seniorId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'voicemail_flag', filter: `senior_user_id=eq.${seniorId}` },
        (payload) => {
          const row = (payload.new ?? {}) as { pending?: boolean };
          if (row.pending !== undefined) setPending(row.pending);
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [seniorId]);

  return { pending, loading };
}
